import React, { useEffect, useState, createRef, useCallback } from 'react';
import Modal from '@mui/material/Modal';
import Paper from '@mui/material/Paper';
import { makeStyles } from '@mui/styles';
import GoogleMapReact from 'google-map-react';
import { maptStyle } from './mapStyles';
import { CustomTextField } from '../TextInput/CustomTextField';
import { Autocomplete } from '@react-google-maps/api';
import { FONT_ENUM } from '../../types/global/fonts.types';
import closeIcon from '../../asset/images/icons/es_x_close.svg';
import './mapModal.css';
import { Box, Button, Slide } from '@mui/material';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { MapCard } from './MapCard';
import { sunset } from '../../asset/images';
import { GPSIconWithUser } from '../GPSIconWithUser/GPSIconWithUser';
import { SentryCapture } from '../../analytics/Sentry';
import CustomLazyLoadImage from '../Image/CustomLazyLoadImage';
import { useSelector } from 'react-redux';
import { userLocationSelector } from './selector';
import { MAP_LIBRARIES } from '../../utils/AppUtils';
import { useGoogleMapsLoader } from '../../hook/useMapLoader';

interface MapModalProps {
    isOpen: boolean;
    onClose: () => void;
    mapLocations?: MapItem[];
    onSelectClick?: (mapItem: MapItem) => void;
    selectionType?: 'single' | 'multiple';
}

const useStyles = makeStyles(theme => ({
    modal: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 8,
    },
    paper: {
        backgroundColor: 'black', // Dark mode background
        border: '2px solid #000',
        borderRadius: 20,
        overflow: 'hidden',
        zIndex: 8,
        // boxShadow: theme.shadows[5],
        // padding: theme.spacing(2, 4, 3),
    },
    map: {
        height: '85vh',
        width: '70vw',
    },
    search: {
        position: 'absolute',
        borderRadius: 20,
        width: 300,
        zIndex: 999,
        top: '9%',
        left: '16%',
    },
    searchIcon: {
        padding: '0, 2',
        height: '100%',
        position: 'absolute',
        pointerEvents: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    '@media (max-width: 600px)': {
        map: {
            height: '100vh',
            width: '100vw', // Dark mode background for screens below 600px wide
        },
        paper: {
            backgroundColor: 'black', // Dark mode background
            border: '2px solid #000',
            borderRadius: 0,
            overflow: 'hidden',
            // boxShadow: theme.shadows[5],
            // padding: theme.spacing(2, 4, 3),
        },
        search: {
            position: 'absolute',
            borderRadius: 20,
            width: '100%',
            zIndex: 999,
            top: '0%',
            left: '0%',
            padding: '10px',
        },
    },
}));

const nigeriaLocations = [
    {
        name: 'Lagos',
        lat: 6.5244,
        lng: 3.3792,
    },
    {
        name: 'Abuja',
        lat: 9.0579,
        lng: 7.4951,
    },
    {
        name: 'Kano',
        lat: 12.0022,
        lng: 8.591956,
    },
    {
        name: 'Ibadan',
        lat: 7.3775,
        lng: 3.947,
    },
    {
        name: 'Port Harcourt',
        lat: 4.8156,
        lng: 7.0498,
    },
];

const DEFAULT_CENTER = { lat: 0, lng: 0 }; // or pick something sensible for your app

export type MapItem = {
    imageUrl: string;
    addressDescription: string;
    coord: string[];
    additionalDetail: string;
};
const MapModal: React.FC<MapModalProps> = ({ isOpen, onClose, onSelectClick }) => {
    const classes = useStyles();
    const [coords, setCoords] = useState<{ lat: number; lng: number }>(() => ({
        lat: 0,
        lng: 0,
    }));
    const [refs, setRefs] = useState<React.RefObject<any>[]>([]);
    const [autoComplete, setAutoComplete] = useState<any>(null);
    const [selectedLocation, setSelectedLocation] = useState<MapItem>();
    const userCurrentLocation = useSelector(userLocationSelector) || {};
    const { device, themeStyle } = useThemeContext();

    const { isLoaded } = useGoogleMapsLoader();

    useEffect(() => {
        const mappedRefs = Array(nigeriaLocations.length)
            .fill('X')
            .map((_, i) => refs[i] || createRef());
        setRefs(mappedRefs);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleLocationChecks = useCallback(async () => {
        if (userCurrentLocation && userCurrentLocation.coordinates) {
            setCoords({
                lat: Number(userCurrentLocation.coordinates[0]),
                lng: Number(userCurrentLocation.coordinates[1]),
            });
        } else {
            try {
                let permissionStatus: any;
                if (navigator && navigator.permissions && navigator.permissions.query) {
                    permissionStatus = await navigator.permissions.query({ name: 'geolocation' });
                }
                if (permissionStatus.state === 'granted') {
                    return;
                } else {
                    navigator.geolocation.getCurrentPosition(({ coords: { latitude, longitude } }) => {
                        if (setCoords && latitude && longitude) {
                            setCoords({
                                lat: Number(latitude),
                                lng: Number(longitude),
                            });
                        }
                    });
                }
            } catch (error) {
                SentryCapture(error, 'error');
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        // Check if location has been set
        handleLocationChecks();
    }, [handleLocationChecks]);

    const handleOnLoad = (autoC: any) => {
        setAutoComplete(autoC);
    };
    const onPlaceChanges = () => {
        if (autoComplete) {
            const lat = autoComplete.getPlace()?.geometry?.location.lat();
            const lng = autoComplete.getPlace()?.geometry?.location.lng();
            const place = autoComplete.getPlace();
            const address = place.formatted_address;
            let photoReference = sunset;
            if (place.photos && place.photos.length > 0) {
                photoReference = place.photos[0].getUrl();
            }
            setSelectedLocation({
                addressDescription: address,
                additionalDetail: `${lat}, ${lng}`,
                coord: [lng, lat],
                imageUrl: photoReference,
            });

            setCoords({
                lat,
                lng,
            });
        }
    };

    const CustomDiv = ({ lat, lng, children }: any) => {
        return <div>{children}</div>;
    };

    const handleOnSelect = () => {
        if (onSelectClick && selectedLocation) {
            onSelectClick(selectedLocation);
        }
    };

    const handleDismiss = () => {
        setSelectedLocation(undefined);
    };

    return (
        <Modal open={isOpen} onClose={onClose} className={classes.modal}>
            <Slide direction="up" in={isOpen} mountOnEnter unmountOnExit>
                <Paper className={classes.paper}>
                    <div className={classes.map}>
                        {isLoaded && (
                            <Autocomplete
                                className={classes.search}
                                onPlaceChanged={onPlaceChanges}
                                onLoad={handleOnLoad}
                                options={{ strictBounds: true }}
                                restrictions={{
                                    country: ['us', 'ng', 'gh', 'ke', 'ug'], // Add more locations
                                }}>
                                <CustomTextField
                                    style={{
                                        zIndex: 7,
                                    }}
                                    textFieldProps={{
                                        InputProps: {
                                            disableUnderline: true,
                                            style: {
                                                fontSize: 12,
                                                fontFamily: FONT_ENUM.REGULAR_HEADER,
                                            },
                                        },
                                    }}
                                />
                            </Autocomplete>
                        )}
                        <Box
                            sx={{
                                position: 'absolute',
                                right: '16%',
                                top: '10%',
                                zIndex: 999,
                            }}>
                            {!device.isMobile && (
                                <CustomLazyLoadImage
                                    src={closeIcon}
                                    style={{ height: '30px', width: '30px' }}
                                    onClick={onClose}
                                    alt="close_icon"
                                />
                            )}
                        </Box>
                        {selectedLocation && !device.isMobile && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    right: '16%',
                                    top: '30%',
                                    zIndex: 999,
                                }}>
                                <MapCard
                                    imageUrl={selectedLocation.imageUrl}
                                    additionalDetail={selectedLocation.additionalDetail}
                                    addressDescription={selectedLocation.addressDescription}
                                    onDismissClick={handleDismiss}
                                    themeStyle={themeStyle}
                                    onSelectClick={handleOnSelect}
                                    sx={{
                                        backgroundColor: theme[themeStyle].deep_grey,
                                    }}
                                />
                            </Box>
                        )}
                        {isLoaded && (
                            <GoogleMapReact
                                bootstrapURLKeys={{
                                    key: 'AIzaSyDRHSBEt5bK6rWO0eD3OslkhlKQg0-W4zA',
                                    libraries: MAP_LIBRARIES,
                                    id: 'thspian_places',
                                }}
                                center={coords}
                                defaultCenter={DEFAULT_CENTER}
                                margin={[50, 50, 50, 50]}
                                options={{ disableDefaultUI: true, zoomControl: true, styles: maptStyle }}
                                onChange={e => {
                                    if (
                                        e.center.lat &&
                                        typeof e.center.lat === 'number' &&
                                        e.center.lng &&
                                        typeof e.center.lng === 'number'
                                    ) {
                                        setCoords({
                                            lat: e.center.lat,
                                            lng: e.center.lng,
                                        });
                                    }
                                }}
                                onChildClick={() => {}}
                                defaultZoom={14}>
                                {/* You can add markers or custom map components here */}
                                {nigeriaLocations.map((loc, i) => (
                                    <CustomDiv lat={loc.lat} lng={loc.lng} key={i}>
                                        <GPSIconWithUser centerImage={''} size={100} />
                                    </CustomDiv>
                                ))}
                            </GoogleMapReact>
                        )}
                        {selectedLocation && device.isMobile && (
                            <Box
                                sx={{
                                    zIndex: 999,
                                    position: 'absolute',
                                    bottom: '15%',
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                }}>
                                <MapCard
                                    imageUrl={selectedLocation.imageUrl}
                                    additionalDetail={selectedLocation.additionalDetail}
                                    addressDescription={selectedLocation.addressDescription}
                                    onSelectClick={handleOnSelect}
                                    themeStyle={themeStyle}
                                    onDismissClick={handleDismiss}
                                    sx={{
                                        backgroundColor: theme[themeStyle].deep_grey,
                                    }}
                                />
                            </Box>
                        )}
                        {device.isMobile && (
                            <Button
                                onClick={onClose}
                                style={{
                                    height: '40px',
                                    width: '70vw',
                                    padding: '10px',
                                    borderRadius: 100,
                                    fontSize: 14,
                                    fontWeight: '600',
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 8,
                                    zIndex: 999,
                                    position: 'absolute',
                                    bottom: '5%',
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                }}
                                sx={{
                                    textDecoration: 'none',
                                    backgroundColor: theme[themeStyle].accountGray,
                                    color: theme[themeStyle].primary_white,
                                    border: `1px solid ${theme[themeStyle].primary_white}`,
                                    textTransform: 'capitalize',
                                    width: 'fit-content',
                                }}>
                                Close Map
                            </Button>
                        )}
                    </div>
                </Paper>
            </Slide>
        </Modal>
    );
};

export { MapModal };
